import StepShell from "./StepShell";
import { fmtMoney } from "../../config";

/**
 * Step 3 — Trade.
 *
 * "Do you have a vehicle you might want to trade in?" → Yes / No / Not sure yet.
 *
 * When Yes, collect:
 *   • Year, Make, Model, Mileage
 *   • Payoff amount
 *   • Low + High appraisal values
 *
 * Then show three selectable trade values:
 *   • Low  = appraisal_low
 *   • Mid  = exact midpoint of low + high
 *   • High = appraisal_high − $500
 *
 * Helper text: "Most appraisals come back as a range. Pick the number you
 * want to use for your estimate. Scott will review the final value with you."
 */
const OPTIONS = [
  { value: "yes", label: "Yes", helper: "I want to include a trade." },
  { value: "no", label: "No", helper: "No trade for this deal." },
  { value: "not_sure", label: "Not sure yet", helper: "We can decide together later." },
];

export default function TradeStep({ byod, onContinue, onBack, firstName }) {
  const trade = byod.state.trade || {};
  const setTrade = (patch) => byod.update("trade", patch);
  const appraisals = byod.tradeAppraisalValues;

  const canContinue = !!trade.has_trade;
  // If Yes, require the selectable trade value if appraisals are present.
  const yesNeedsRangeValid = trade.has_trade === "yes" && appraisals.valid && !trade.selected_value;

  return (
    <StepShell
      title={firstName ? `${firstName}, do you have a vehicle you might want to trade in?` : "Do you have a vehicle you might want to trade in?"}
      subtitle="Including a trade can lower your taxable amount and reduce your monthly payment."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!canContinue || yesNeedsRangeValid}
      testIdPrefix="byod-trade-step"
    >
      <div className="grid sm:grid-cols-3 gap-3">
        {OPTIONS.map((opt) => {
          const selected = trade.has_trade === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTrade({ has_trade: opt.value, ...(opt.value !== "yes" ? { selected_value: null } : {}) })}
              className={`text-left rounded-2xl border-2 p-4 transition ${selected ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
              data-testid={`byod-trade-option-${opt.value}`}
              aria-pressed={selected}
            >
              <div className="font-extrabold text-[var(--ssc-navy)]">{opt.label}</div>
              <p className="text-xs text-[var(--ssc-ink-2)] mt-1">{opt.helper}</p>
            </button>
          );
        })}
      </div>

      {trade.has_trade === "yes" && (
        <div className="mt-6 space-y-5" data-testid="byod-trade-details">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Year">
              <input className="ssc-input" inputMode="numeric" value={trade.year || ""} onChange={(e) => setTrade({ year: e.target.value })} data-testid="byod-trade-year" />
            </Field>
            <Field label="Make">
              <input className="ssc-input" value={trade.make || ""} onChange={(e) => setTrade({ make: e.target.value })} data-testid="byod-trade-make" />
            </Field>
            <Field label="Model">
              <input className="ssc-input" value={trade.model || ""} onChange={(e) => setTrade({ model: e.target.value })} data-testid="byod-trade-model" />
            </Field>
            <Field label="Mileage">
              <input className="ssc-input" inputMode="numeric" value={trade.mileage || ""} onChange={(e) => setTrade({ mileage: e.target.value })} data-testid="byod-trade-mileage" />
            </Field>
            <Field label="Payoff amount (if any)">
              <input className="ssc-input" inputMode="numeric" value={trade.payoff || ""} onChange={(e) => setTrade({ payoff: e.target.value })} data-testid="byod-trade-payoff" placeholder="$" />
            </Field>
          </div>

          <div className="rounded-2xl border-2 border-[var(--ssc-blue)]/30 bg-[var(--ssc-paper)] p-4">
            <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
              Appraisal range
            </div>
            <p className="text-xs text-[var(--ssc-ink-2)] mt-1 leading-snug">
              If you know the rough range a dealer or online appraiser gave you, drop it in
              here so we can show your three trade-value options.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <Field label="Low appraisal value">
                <input className="ssc-input" inputMode="numeric" value={trade.appraisal_low || ""} onChange={(e) => setTrade({ appraisal_low: e.target.value, selected_value: null })} data-testid="byod-trade-appraisal-low" placeholder="$" />
              </Field>
              <Field label="High appraisal value">
                <input className="ssc-input" inputMode="numeric" value={trade.appraisal_high || ""} onChange={(e) => setTrade({ appraisal_high: e.target.value, selected_value: null })} data-testid="byod-trade-appraisal-high" placeholder="$" />
              </Field>
            </div>
          </div>

          {appraisals.valid && (
            <div data-testid="byod-trade-value-picker">
              <p className="text-sm text-[var(--ssc-ink-2)] leading-snug mb-3">
                Most appraisals come back as a range. Pick the number you want to use for your estimate.
                Scott will review the final value with you.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <ValueCard
                  testId="byod-trade-value-low"
                  selected={trade.selected_value === "low"}
                  onClick={() => setTrade({ selected_value: "low" })}
                  label="Low"
                  amount={appraisals.low}
                  sub="Conservative number"
                />
                <ValueCard
                  testId="byod-trade-value-mid"
                  selected={trade.selected_value === "mid"}
                  onClick={() => setTrade({ selected_value: "mid" })}
                  label="Middle"
                  amount={appraisals.mid}
                  sub="Exact midpoint"
                />
                <ValueCard
                  testId="byod-trade-value-high"
                  selected={trade.selected_value === "high"}
                  onClick={() => setTrade({ selected_value: "high" })}
                  label="High"
                  amount={appraisals.high}
                  sub="$500 below ceiling"
                />
              </div>
              {yesNeedsRangeValid && (
                <p className="text-xs text-[var(--ssc-red)] mt-2" data-testid="byod-trade-pick-warning">
                  Pick a trade value to continue, or change your answer above.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </StepShell>
  );
}

function ValueCard({ label, amount, sub, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border-2 p-4 transition ${selected ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
      data-testid={testId}
      aria-pressed={selected}
    >
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">{label}</div>
      <div className="text-2xl font-black text-[var(--ssc-navy)] mt-0.5">{fmtMoney(amount)}</div>
      <div className="text-xs text-[var(--ssc-ink-2)] mt-1">{sub}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="ssc-label">{label}</span>
      {children}
    </label>
  );
}
